import { AccountPersonal } from '@/components/content/Account/parts/Personal';
import { AccountRecentOrders } from '@/components/content/Account/parts/RecentOrders';
import { usePersonInfo } from '@/data/Content/PersonInfo';
import { ID } from '@/data/types/Basic';
import { Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
	ACCOUNT_TABS,
	ACCOUNT_TABS_ITEMS,
	AccountTabs,
} from '@/components/content/Account/parts/Tabs';
import { Switch } from '@/utils/switch';
import { AccountTitleSX } from '@/components/content/Account/styles/main';
import { AccountFavorites } from '@/components/content/Account/parts/Favorites';
import { ClubDpPoints } from '@/components/content/Account/parts/ClubDpPoints';
import { useClubDpFlags } from '@/data/Content/ClubDp';
import { AddressBook } from '@/components/content/AddressBook';
import { AccountSummary } from '@/components/content/Account/parts/Summary';
import { DppAccountCoupon } from '@/components/blocks/DpRepurchaseCoupon/parts/DppAccountCoupon';
import { ClubDpRewards } from '@/components/content/Account/parts/ClubDpRewards/ClubDpRewards';
import { useLogout } from '@/data/Content/Logout';
import { DpContainer } from '@/components/blocks/DpContainer';
import { updateHotSearchParams } from '@/utils/updateHotSearchParams';
import { getHotSearchParam, useHotSearchParam } from '@/utils/getHotSearchParam';
import { BenefitsGrayContentRecommendation } from '@/components/blocks/BenefitsGrayContentRecommendation';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';

export enum ACCOUNT_QUERY_TABS {
	PERSONAL_INFORMATION = 'personal',
	ORDERS_HISTORY = 'orders',
	FAVORITES = 'favorites',
	RESUME = 'resume',
	LOYALTY_PROGRAM = 'loyalty',
	ADDRESSES = 'addresses',
	CLUBDP_POINTS = 'clubdp',
}

const extractTabByQuery = (tab: ACCOUNT_QUERY_TABS | undefined): ACCOUNT_TABS | undefined =>
	Switch(tab)
		.case(ACCOUNT_QUERY_TABS.PERSONAL_INFORMATION, () => ACCOUNT_TABS.PERSONAL_INFORMATION)
		.case(ACCOUNT_QUERY_TABS.ORDERS_HISTORY, () => ACCOUNT_TABS.ORDERS_HISTORY)
		.case(ACCOUNT_QUERY_TABS.FAVORITES, () => ACCOUNT_TABS.FAVORITES)
		.case(ACCOUNT_QUERY_TABS.RESUME, () => ACCOUNT_TABS.RESUME)
		.case(ACCOUNT_QUERY_TABS.LOYALTY_PROGRAM, () => ACCOUNT_TABS.LOYALTY_PROGRAM)
		.case(ACCOUNT_QUERY_TABS.ADDRESSES, () => ACCOUNT_TABS.ADDRESSES)
		.case(ACCOUNT_QUERY_TABS.CLUBDP_POINTS, () => ACCOUNT_TABS.CLUBDP_POINTS)
		.defaultTo(() => undefined);

export const Account: FC<{ id: ID }> = () => {
	const searchTab = useHotSearchParam('tab');

	useEffect(() => {
		if (!searchTab) {
			return;
		}

		const redirectTab = extractTabByQuery(searchTab as ACCOUNT_QUERY_TABS);
		if (redirectTab) {
			setSelectedTab(redirectTab);
		}
	}, [searchTab]);

	const { personInfo } = usePersonInfo();
	const [selectedTab, setSelectedTab] = useState<ACCOUNT_TABS>(ACCOUNT_TABS.SUMMARY);
	const { handleLogout } = useLogout();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { flags, mutateClubDpFlags } = useClubDpFlags();

	const tabs = useMemo(() => {
		if (flags?.linked?.puntosDpVale) {
			return ACCOUNT_TABS_ITEMS;
		}

		return ACCOUNT_TABS_ITEMS.filter((tab) => tab.id !== ACCOUNT_TABS.CLUBDP_POINTS);
	}, [flags]);

	const handleTabSelect = async (tab: ACCOUNT_TABS) => {
		setSelectedTab(tab);
		updateHotSearchParams('tab', ACCOUNT_QUERY_TABS[tab as keyof typeof ACCOUNT_QUERY_TABS]);
	};

	return (
		<React.Fragment>
			<DpContainer>
				<Stack direction="column" marginTop={3}>
					<Typography component={'h2'} sx={AccountTitleSX}>
						{'HOLA '}
						{`${personInfo?.firstName},`}
					</Typography>
					<Typography>
						{
							'Aquí tienes el resumen de tu cuenta. Selecciona una de las siguientes categorías para ver más información de la misma.'
						}
					</Typography>
				</Stack>
				<Divider sx={{ marginTop: 1, marginBottom: 1, borderColor: 'transparent' }} />
				<AccountTabs items={tabs} select={selectedTab} onSelect={handleTabSelect} />
				<Divider sx={{ marginTop: 2, marginBottom: 2, borderColor: 'transparent' }} />
				{Switch(selectedTab)
					.case(ACCOUNT_TABS.PERSONAL_INFORMATION, () => <AccountPersonal />)
					.case(ACCOUNT_TABS.ADDRESSES, () => <AddressBook id={'ID'} />)
					.case(ACCOUNT_TABS.LOYALTY_PROGRAM, () => (
						<ClubDpRewards onMutateClubDpFlags={mutateClubDpFlags} />
					))
					.case(ACCOUNT_TABS.CLUBDP_POINTS, () => (
						<ClubDpPoints onTabSelect={handleTabSelect} onMutateClubDpFlags={mutateClubDpFlags} />
					))
					.case(ACCOUNT_TABS.ORDERS_HISTORY, () => <AccountRecentOrders />)
					.case(ACCOUNT_TABS.FAVORITES, () => <AccountFavorites />)
					.defaultTo(() => (
						<>
							<DppAccountCoupon />
							<AccountSummary personInfo={personInfo} onSelectedTab={handleTabSelect} />
						</>
					))}

				<Divider sx={{ marginTop: 3, marginBottom: 3, borderColor: 'transparent' }} />
			</DpContainer>
			{isMobile ? (
				<Stack
					display={'flex'}
					direction={'row'}
					justifyContent={'center'}
					alignItems={'center'}
					pb={'30px'}
				>
					<DpButton variant="text" onClick={handleLogout}>
						{'Cerrar sesión'}
					</DpButton>
				</Stack>
			) : null}

			<BenefitsGrayContentRecommendation />
		</React.Fragment>
	);
};
